<template>
    <div v-if="can">
        <settings-heading
            :title="title"
        >
            <more-info-button
                only-use-icon
                hide-label
                position="right"
            >
                <template #content>
                    <b class="block mb-4">{{ $t('general.more_info') }}</b>
                    <p v-html="tooltip"></p>
                </template>
            </more-info-button>
        </settings-heading>

        <div class="mb-48">
            <loading v-if="!templates.length && fetchingTemplates" class="pt-16"></loading>
            <template v-else>
                <div class="flex justify-between">
                    <new-module-template
                        :service="service"
                        :template="activeTemplate"
                        @input="openTemplate($event.id)"
                    />
                    <div v-if="interviewRoute" class="flex items-center">
                        <simple-button
                            @click="manageQuestions"
                        >
                            {{ $t('question.manage') }}
                        </simple-button>
                    </div>
                </div>


                <div v-if="templates.length" class="flex-grow w-full lg:flex">
                    <div class="border-b-2 border-primary-5 xl:border-b-0 xl:shrink-0 bg-white">
                        <div class="h-full py-6 lg:w-320 min-h-128">
                            <h3 class="text-body font-semibold mb-16">
                                {{ title }}
                            </h3>

                            <button
                                v-if="showUngroupedItems && ungroupedItems.length"
                                :class="templateClass(ungroupedItemsTemplate)"
                                title="Open template"
                                @click="openTemplate(ungroupedItemsTemplate.id)">
                                <span :class="hoverOrSelected(ungroupedItemsTemplate)"></span>
                                <label-with-missing-translation :label="ungroupedItemsTemplate.title[this.locale] || null"/>
                                <tooltip
                                    content="Hier worden alle filters opgelijst die niet in een filter groep zitten"
                                    position="right">
                                    <svg-icon class="max-w-16 max-h-16 text-primary ml-8" type="info"/>
                                </tooltip>
                            </button>

                            <draggable
                                v-bind="dragOptions"
                                v-if="templates.length > 0"
                                v-model="templates"
                                handle=".draggable"
                                :item-key="item => `template-${item.id}`"
                                @end="updateOrder"
                                @start="drag = true"
                            >
                                <template #item="{ element }">
                                    <button
                                        :class="templateClass(element)"
                                        class="justify-between"
                                        title="Open template"
                                        @click="openTemplate(element.id)"
                                        @mousedown="handleDrag(element)"
                                    >
                                        <span :class="hoverOrSelected(element)"></span>
                                        <label-with-missing-translation :label="element.titleLocale"/>
                                        <span class="draggable cursor-move flex mr-8 group">
                                            <svg-icon class="max-h-16 w-16" type="drag"/>
                                        </span>
                                    </button>
                                </template>
                            </draggable>
                        </div>
                    </div>

                    <div class="bg-white pl-32 w-full">
                        <module-template-items
                            :template="activeTemplate"
                            :service="service"
                            @reset="activeTemplate = null"
                        />
                        <slot></slot>
                    </div>
                </div>
            </template>
        </div>

        <questions
            v-if="managingQuestions"
            @close="stopManagingQuestions"
            @delete="onQuestionDelete"
        />
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import {mapState, mapActions} from 'pinia';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import {ADD_NOTIFICATION} from '../../state/mutation-types';

import {normalizeJson} from '../../../shared/helpers';

import Loading from '../../components/ui/Loading.vue';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import draggable from 'vuedraggable';
import SvgIcon from '../../components/ui/SvgIcon.vue';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import Error403 from '../../components/ui/Error403.vue';
import MoreInfoButton from '../../components/ui/MoreInfoButton.vue';
import Input from '../../components/forms/Input.vue';
import ClickEditInput from '../../components/forms/ClickEditInput.vue';
import ModuleTemplateItems from '../../components/ModuleTemplateItems.vue';
import SelectDropdown from "../../components/forms/SelectDropdown.vue";
import NewModuleTemplate from "../../components/applications/NewModuleTemplate.vue";
import LabelWithMissingTranslation from "../../components/LabelWithMissingTranslation";
import Tooltip from "../../components/navigation/Tooltip.vue";
import Questions from "../../components/Questions.vue";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";
import {useRepo} from "pinia-orm";
import {useSortBy} from "pinia-orm/helpers";
import CompetenceTemplate from "../../models/CompetenceTemplate";
import InterviewTemplate from "../../models/InterviewTemplate";
import FilterGroup from "../../models/FilterGroup";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

export default {
    components: {
        SettingsHeading,
        Questions,
        'click-edit-input': ClickEditInput,
        'more-info-button': MoreInfoButton,
        'simple-input': Input,
        'loading': Loading,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'error-403': Error403,
        ModuleTemplateItems,
        NewModuleTemplate,
        draggable,
        SelectDropdown,
        Tooltip,
        LabelWithMissingTranslation,
    },
    props: {
        service: null,
        hideEmptyTranslations: false,
        showUngroupedItems: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            fetchingTemplates: true,
            itemsLoaded: false,
            activeTemplate: null,
            newTemplate: '',
            creatingTemplate: false,
            drag: false,
            saving: false,
            currentTemplate: null,
            managingQuestions: false,
            ungroupedItemsTemplate: null,
            models: {
                'competence-templates': CompetenceTemplate,
                'interview-templates': InterviewTemplate,
                'filter-groups': FilterGroup,
            }
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'templateModuleInfo']),
        can() {
            return this.$can('settings.applications');
        },
        title() {
            return this.moduleInfo.text.title;
        },
        interviewRoute() {
            return this.$route.name === 'settings.interview-templates';
        },
        dragOptions() {
            return {
                animation: 200,
                group: 'templates',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
        templates: {
            get() {
                let result = this.model.orderBy('order');

                if (this.hideEmptyTranslations) {
                    result = result.where('title', (value) => value);
                }

                return result.get();
            },
            set(templates) {
                this.resort(templates);
            },
        },
        moduleInfo() {
            return this.templateModuleInfo;
        },
        model() {
            return useRepo(this.models[this.moduleInfo.templateModel]);
        },
        ungroupedItems() {
            return this.service.ungroupedFiltersORM(
                this.ungroupedItemsTemplate,
                this.model,
                this.moduleInfo.children
            );
        },
        tooltip() {
            return this.moduleInfo.text.tooltip || this.$t('settings.interview-templates.description', {
                type: this.$t(`settings.${this.moduleInfo.templateModel}.single`).toLowerCase(),
                type_m: this.$t(`settings.${this.moduleInfo.templateModel}.title`).toLowerCase()
            });
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        currentTemplateIsActive(template) {
            return this.activeTemplate && this.activeTemplate.id === template.id;
        },
        handleDrag(template) {
            this.currentTemplate = template;
        },
        templateClass(template) {
            let classes = 'rounded-6 relative w-full px-12 py-10 group flex items-center mb-8';
            return classes + ' ' + (this.currentTemplateIsActive(template) ? 'bg-primary-5' : 'bg-primary-5 hover:bg-primary-5');
        },
        hoverOrSelected(template) {
            return "absolute inset-y-0 left-0 w-0 bg-secondary-80 rounded-l-sm group-hover:w-4" + (this.currentTemplateIsActive(template) ? " w-4" : "");
        },
        async fetchTemplates() {
            return await this.service.index(true).then(result => {
                this.model.save(normalizeJson(result.data));
            });
        },
        openTemplate(id) {
            if (id) {
                this.activeTemplate = this.model
                    .where('id', id)
                    .with(this.moduleInfo.children)
                    .orderBy((item) => {
                        item[this.moduleInfo.children] = useSortBy(item[this.moduleInfo.children], [['pivot.order', 'asc']]);
                    })
                    .first();
            } else {
                this.activeTemplate = this.ungroupedItemsTemplate
            }

            this.$emit('selected', this.activeTemplate);
        },
        resort(sortedTemplates) {
            let sort = sortedTemplates.map((template, index) => ({id: template.id, order: index}));
            let original = this.model.find(sort.map(sort => sort.id)).map(templates => ({
                id: templates.id,
                order: templates.order,
            }));

            this.model.save(sort);
        },
        updateOrder(evt) {
            if (evt.newDraggableIndex === this.currentTemplate.order) {
                this.currentTemplate = null;
                this.drag = false;
                return;
            }

            this.service.updateSortOrders({
                order: this.templates.map(function (template, index) {
                    return {id: template.id, order: index}
                })
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: this.$t(`settings.${this.moduleInfo.templateModel}.title`).toLowerCase()}),
                });
            });

            this.currentTemplate = null;
            this.drag = false;
        },
        manageQuestions() {
            this.managingQuestions = true;
        },
        stopManagingQuestions() {
            this.managingQuestions = false;
        },
        onQuestionDelete() {
            this.fetchTemplates();
            this.openTemplate(this.activeTemplate.id);
        }
    },
    mounted() {
        this.fetchingTemplates = true;

        this.fetchTemplates().then(() => {
            this.fetchingTemplates = false;

            if (this.showUngroupedItems && this.ungroupedItems.length > 0) {
                this.activeTemplate = this.ungroupedItemsTemplate;
            } else {
                this.activeTemplate = this.model
                    .where((row) => Number.isInteger(row.id))
                    .with(this.moduleInfo.children)
                    .orderBy((item) => {
                        item[this.moduleInfo.children] = useSortBy(item[this.moduleInfo.children], [['pivot.order', 'asc']]);
                    })
                    .first();
            }

            this.$emit('selected', this.activeTemplate);
        }).then(() => {
            this.itemsLoaded = true;
        });
    },
};
</script>
